import { useCallback, useEffect, useMemo, useState } from 'react'
import { orderBy, sortBy } from 'lodash'
import { DateTime } from 'luxon'
import { faClose } from '@fortawesome/pro-regular-svg-icons'
import {
  Badge,
  Link,
  Table,
  Text,
  Button,
  PaginationPages,
  Alert,
  Select,
  selectValsToDropdownOptions,
  Separator,
  Loader,
} from '@/ui'
import {
  EXPENSE_BREAKDOWN_PAGE_SIZE,
  FinancialInsightsButtonType,
  INSIGHTS_TYPE,
  InsightsRowAPIResponse,
  InsightsTransactionsAPIResponse,
  MONTHS_ARR,
  applyCategoryFilter,
  computeEndDateForInsightsExpensesBreakdownTable,
  generateSampleInsightsRowAPIResponses,
  generateSampleInsightsTransactionsAPIResponses,
  getIncomeOrExpenseLabel,
  isTopExpense,
} from './utils'
import {
  fetchInsightsCategoryTransactions,
  fetchInsightsTableData,
  FETCH_INSIGHTS_TABLE_DATA_KEY,
  FETCH_INSIGHTS_CATEGORY_TRANSACTIONS_KEY,
  FETCH_INSIGHTS_TABLE_DROPDOWN_KEY,
} from './financeActions'
import { TextColors, TypographyStyles } from '@/styles'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import {
  useEndDateForFinancialInsights,
  useReselector,
  useTrackFinancialInsightsButtonClick,
} from '@/utils/sharedHooks'
import {
  selectFirstErrorMessageForKeys,
  selectIsFetchingForKeys,
} from '@/reducers/fetch'
import { useAppDispatch } from '@/utils/typeHelpers'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'

import { ExpensesBreakdownTransactions } from '../expenses/ExpensesBreakdownTransactions'
import { DropdownType, InsightsDividedCard } from './InsightsDividedCard'

import { InsightsRow } from '../expenses/InsightsRow'
import { FreeTrialUnreviewedAlert } from '@/components/FreeTrialUnreviewedAlert'

export const InsightsBreakdownTable = ({
  inputDate,
  insightsType,
  shouldDisplaySampleData = false,
  years,
}: {
  inputDate?: string
  insightsType: INSIGHTS_TYPE
  shouldDisplaySampleData?: boolean
  years: number[]
}) => {
  const dispatch = useAppDispatch()
  const [insightsRows, setInsightsRows] = useState<InsightsRowAPIResponse[]>([])
  const [filteredInsightsRows, setFilteredInsightsRows] = useState<
    InsightsRowAPIResponse[]
  >([])
  const [currentPageInsightsRows, setCurrentPageInsightsRows] = useState<
    InsightsRowAPIResponse[]
  >([])
  const [currentPage, setCurrentPage] = useState(1)
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [yearFilter, setYearFilter] = useState<number>(years[0])
  const [monthFilter, setMonthFilter] = useState(MONTHS_ARR[0].value)
  const [yearOptions, setYearOptions] = useState<number[]>([])
  const [selectedInsightsRow, setSelectedInsightsRow] =
    useState<InsightsRowAPIResponse | null>(null)
  const [insightTransactions, setInsightsTransactions] = useState<
    InsightsTransactionsAPIResponse[]
  >([])
  const [currentTransactions, setCurrentTransactions] = useState<
    InsightsTransactionsAPIResponse[]
  >([])
  const [transactionsPages, setTransactionsPages] = useState(1)
  const [topExpenseCategoryIds, setTopExpenseCategoryIds] = useState<number[]>(
    []
  )
  const [isYearOptionsEmpty, setIsYearOptionsEmpty] = useState(false)

  const currentDate = useMemo(
    () =>
      inputDate
        ? DateTime.fromFormat(inputDate, DATE_FORMATS_LUXON.INPUT)
        : DateTime.now(),
    [inputDate]
  )

  const insightEndDate = useEndDateForFinancialInsights(currentDate)
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const trackInsights = useTrackFinancialInsightsButtonClick()

  useEffect(() => {
    setYearOptions(years)
    setYearFilter(years[0])
  }, [dispatch, shouldDisplaySampleData, years, isYearOptionsEmpty])

  const { filteredYearOptions, filteredMonthOptions } = useMemo(() => {
    const filteredYears = [
      insightEndDate.year,
      ...yearOptions.filter((year) => year < insightEndDate.year),
    ]
    if ((yearFilter ?? 0) > filteredYears[0]) {
      setYearFilter(filteredYears[0])
    }
    let filteredMonths = [...MONTHS_ARR]
    if (yearFilter === insightEndDate.year) {
      filteredMonths = filteredMonths.filter(
        (monthOption) =>
          monthOption.value === 'ytd' ||
          parseInt(monthOption.value) < insightEndDate.month
      )
    }
    return {
      filteredYearOptions: selectValsToDropdownOptions(filteredYears),
      filteredMonthOptions: filteredMonths,
    }
  }, [yearOptions, yearFilter, insightEndDate])

  useEffect(() => {
    const fetchBreakdownData = async (startDate: string, endDate: string) => {
      const breakdownData = shouldDisplaySampleData
        ? generateSampleInsightsRowAPIResponses()
        : await fetchInsightsTableData(
            insightsType,
            startDate,
            endDate
          )(dispatch)

      if (breakdownData && breakdownData.length < 1) {
        setIsYearOptionsEmpty(true)
      }

      const sortedBreakdown = orderBy(
        breakdownData,
        'transactions_sum_in_cents',
        insightsType === INSIGHTS_TYPE.EXPENSES ? 'asc' : 'desc'
      )

      const topExpenseCategoryIds = sortedBreakdown
        .slice(0, 3)
        .map((expenseBreakdown) => expenseBreakdown.transaction_category_id)

      setTopExpenseCategoryIds(topExpenseCategoryIds)

      setInsightsRows(sortedBreakdown)
    }

    if (!yearFilter) {
      return
    }

    const startMonthNumber = monthFilter === 'ytd' ? '0' : monthFilter
    const startDate = DateTime.fromObject({
      year: yearFilter,
      month: parseInt(startMonthNumber) + 1,
      day: 1,
    })

    const endDate = computeEndDateForInsightsExpensesBreakdownTable(
      startDate,
      insightEndDate,
      yearFilter,
      monthFilter
    )

    const formattedStartDate = startDate.toFormat(DATE_FORMATS_LUXON.TIMESTAMP)
    const formattedEndDate = endDate.toFormat(DATE_FORMATS_LUXON.TIMESTAMP)

    fetchBreakdownData(formattedStartDate, formattedEndDate)
  }, [
    yearFilter,
    monthFilter,
    dispatch,
    currentDate,
    insightEndDate,
    insightsType,
    shouldDisplaySampleData,
  ])

  useEffect(() => {
    const categoryFilteredExpensesBreakdown = applyCategoryFilter(
      insightsRows,
      categoryFilter,
      topExpenseCategoryIds
    )
    setFilteredInsightsRows(categoryFilteredExpensesBreakdown)
  }, [categoryFilter, insightsRows, topExpenseCategoryIds])

  useEffect(() => {
    const pageRows = filteredInsightsRows.slice(
      (currentPage - 1) * EXPENSE_BREAKDOWN_PAGE_SIZE,
      currentPage * EXPENSE_BREAKDOWN_PAGE_SIZE
    )
    setCurrentPageInsightsRows(pageRows)
  }, [currentPage, filteredInsightsRows, currentTransactions])

  useEffect(() => {
    const fetchInsightsTransactionData = async (
      transactionCategoryId: number,
      startDate: string,
      endDate: string
    ) => {
      const fetchedTransactions = shouldDisplaySampleData
        ? generateSampleInsightsTransactionsAPIResponses(
            transactionCategoryId,
            insightsRows
          )
        : await fetchInsightsCategoryTransactions(
            transactionCategoryId,
            startDate,
            endDate
          )(dispatch)

      if (fetchedTransactions) {
        const orderdDescTransactions = sortBy(
          fetchedTransactions,
          'date'
        ).reverse()

        setInsightsTransactions(orderdDescTransactions)
        setTransactionsPages(
          Math.ceil(fetchedTransactions.length / EXPENSE_BREAKDOWN_PAGE_SIZE)
        )
        setCurrentTransactions(
          orderdDescTransactions.slice(0, EXPENSE_BREAKDOWN_PAGE_SIZE)
        )
      }
    }

    if (yearFilter && monthFilter && selectedInsightsRow) {
      const { transaction_category_id } = selectedInsightsRow
      const startMonthNumber = monthFilter === 'ytd' ? '0' : monthFilter

      const startDate = DateTime.fromObject({
        year: yearFilter,
        month: parseInt(startMonthNumber) + 1,
        day: 1,
      })

      const endDate = computeEndDateForInsightsExpensesBreakdownTable(
        startDate,
        insightEndDate,
        yearFilter,
        monthFilter
      )

      fetchInsightsTransactionData(
        transaction_category_id,
        startDate.toFormat(DATE_FORMATS_LUXON.TIMESTAMP),
        endDate.toFormat(DATE_FORMATS_LUXON.TIMESTAMP)
      )
    }
  }, [
    selectedInsightsRow,
    dispatch,
    monthFilter,
    yearFilter,
    insightEndDate,
    shouldDisplaySampleData,
    insightsRows,
  ])

  const handleCategoryFilterChange = (category: string) => {
    setCurrentPage(1)
    setCategoryFilter(category)
    trackInsights({
      button:
        insightsType === INSIGHTS_TYPE.EXPENSES
          ? FinancialInsightsButtonType.EXPENSES_FILTER
          : FinancialInsightsButtonType.INCOME_FILTER,
      insightsType,
    })
  }

  const handleYearFilterChange = (year: number) => {
    setCurrentPage(1)
    setYearFilter(year)
    trackInsights({
      button: FinancialInsightsButtonType.YEAR_FILTER,
      insightsType,
    })
  }

  const handleMonthFilterChange = (month: string) => {
    setCurrentPage(1)
    setMonthFilter(month)
    trackInsights({
      button: FinancialInsightsButtonType.MONTH_FILTER,
      insightsType,
    })
  }

  const handleSelectedExpenseBreakdown = (
    expenseBreakdown: InsightsRowAPIResponse
  ) => {
    setSelectedInsightsRow(expenseBreakdown)
    setCurrentPage(1)
    trackInsights({
      button: FinancialInsightsButtonType.CATEGORY_TRANSACTIONS,
      insightsType,
    })
  }

  const handlePageChange = useCallback(
    (page: number) => {
      if (selectedInsightsRow) {
        const start = (page - 1) * EXPENSE_BREAKDOWN_PAGE_SIZE
        const end = start + EXPENSE_BREAKDOWN_PAGE_SIZE
        setCurrentTransactions(insightTransactions.slice(start, end))
      }
      setCurrentPage(page)
      trackInsights({
        button: FinancialInsightsButtonType.TABLE_PAGE,
        insightsType,
      })
    },
    [selectedInsightsRow, insightTransactions, trackInsights, insightsType]
  )

  const pages = useMemo(() => {
    if (selectedInsightsRow) {
      return Math.ceil(insightTransactions.length / EXPENSE_BREAKDOWN_PAGE_SIZE)
    } else {
      return Math.ceil(
        filteredInsightsRows.length / EXPENSE_BREAKDOWN_PAGE_SIZE
      )
    }
  }, [
    filteredInsightsRows.length,
    insightTransactions.length,
    selectedInsightsRow,
  ])

  const onTransactionsExitClick = () => {
    setSelectedInsightsRow(null)
    setCurrentPage(1)
  }

  const queryLoading = useReselector(selectIsFetchingForKeys, [
    FETCH_INSIGHTS_TABLE_DROPDOWN_KEY,
    FETCH_INSIGHTS_CATEGORY_TRANSACTIONS_KEY,
    FETCH_INSIGHTS_TABLE_DATA_KEY,
  ])

  const error = useReselector(selectFirstErrorMessageForKeys, [
    FETCH_INSIGHTS_TABLE_DROPDOWN_KEY,
    FETCH_INSIGHTS_CATEGORY_TRANSACTIONS_KEY,
    FETCH_INSIGHTS_TABLE_DATA_KEY,
  ])

  const getCategoryDropdownOptionsForInsightsType = (
    insightsType: INSIGHTS_TYPE
  ) => {
    const commonOptions = insightsRows.map((insightsRow) => ({
      text: insightsRow.transaction_category_name,
      value: insightsRow.transaction_category_name,
    }))

    const typeSpecificOptions =
      insightsType === INSIGHTS_TYPE.EXPENSES
        ? [
            { text: 'All Expenses', value: 'all' },
            { text: 'Top Expenses', value: 'top' },
          ]
        : [{ text: 'All Categories', value: 'all' }]

    return [...typeSpecificOptions, ...commonOptions]
  }

  const displayYearOptionsEmptyMessage = (insightsType: INSIGHTS_TYPE) => {
    return (
      <Alert
        className="w-full"
        variant="default"
        title={`No ${getIncomeOrExpenseLabel(insightsType)} Yet`}
      >
        <Text className="text-center mb-4" color={TextColors.MUTED}>
          Your categorized {getIncomeOrExpenseLabel(insightsType).toLowerCase()}{' '}
          will show up here
        </Text>

        <Link href={'/accounts#connected-institutions'} newPage={false}>
          <Button variant="link">Link Business Account</Button>
        </Link>
      </Alert>
    )
  }

  const displayMonthEmptyMessage = (insightsType: INSIGHTS_TYPE) => {
    return (
      <Alert
        className="w-full"
        variant="default"
        title={`No ${getIncomeOrExpenseLabel(insightsType)}`}
      >
        <Text className="text-center mb-4" color={TextColors.MUTED}>
          You have no {getIncomeOrExpenseLabel(insightsType).toLowerCase()} for
          this month.
        </Text>
      </Alert>
    )
  }

  const displayErrorMessage = () => {
    return (
      <Alert
        className="w-full"
        variant="error"
        title="Something Went Wrong"
        description="Please try again in a bit."
      />
    )
  }

  return (
    <>
      <FreeTrialUnreviewedAlert />

      {selectedInsightsRow ? (
        queryLoading ? (
          <div className="w-full pt-4">
            <Loader active />
          </div>
        ) : (
          <>
            {/* Header for selected category */}
            <div className="w-full flex items-center gap-2 pt-4">
              <Text color={TextColors.MUTED}>
                Showing {selectedInsightsRow.transactions_count} transactions
                for
              </Text>
              <Badge>
                <span className="mr-2">
                  {selectedInsightsRow.transaction_category_name}
                </span>

                <Button
                  onClick={onTransactionsExitClick}
                  variant="discreet"
                  iconLeft={faClose}
                />
              </Badge>
            </div>

            <div className="w-full pt-4 overflow-x-auto">
              <Table className="w-full text-left">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell className="text-left w-1/4">
                      <Text
                        variant={TypographyStyles.EYEBROW}
                        color={TextColors.MUTED}
                      >
                        Date
                      </Text>
                    </Table.HeaderCell>
                    <Table.HeaderCell className="text-left w-1/3">
                      <Text
                        variant={TypographyStyles.EYEBROW}
                        color={TextColors.MUTED}
                      >
                        Description
                      </Text>
                    </Table.HeaderCell>
                    {!isMobile && (
                      <Table.HeaderCell className="text-right w-1/4">
                        <Text
                          variant={TypographyStyles.EYEBROW}
                          color={TextColors.MUTED}
                        >
                          Category
                        </Text>
                      </Table.HeaderCell>
                    )}
                    <Table.HeaderCell className="text-right w-1/5">
                      <Text
                        variant={TypographyStyles.EYEBROW}
                        color={TextColors.MUTED}
                      >
                        Amount
                      </Text>
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  <ExpensesBreakdownTransactions
                    currentTransactions={currentTransactions}
                    transactionCategoryName={
                      selectedInsightsRow.transaction_category_name
                    }
                  />
                </Table.Body>
              </Table>
              <Separator />
            </div>
          </>
        )
      ) : (
        <>
          {/* Filter Dropdowns */}
          <div className="w-full pt-2 flex flex-col sm:flex-row gap-2">
            <InsightsDividedCard
              columns={[
                {
                  content: (
                    <div className="w-full min-w-25">
                      <Select
                        value={yearFilter?.toString() ?? ''}
                        options={filteredYearOptions}
                        onValueChange={(val) => {
                          handleYearFilterChange(parseInt(val ?? '0'))
                        }}
                      />
                    </div>
                  ),
                },
                {
                  content: (
                    <div className="w-full flex-1">
                      <Select
                        value={monthFilter}
                        options={filteredMonthOptions}
                        onValueChange={(val) => {
                          handleMonthFilterChange(val as string)
                        }}
                      />
                    </div>
                  ),
                  isLastColumn: true,
                },
              ]}
              type={DropdownType.dropdown}
            />
            <div className="w-full sm:w-auto sm:min-w-[150px]">
              <Select
                value={categoryFilter}
                options={getCategoryDropdownOptionsForInsightsType(
                  insightsType
                )}
                onValueChange={(val) => {
                  handleCategoryFilterChange(val as string)
                }}
              />
            </div>
          </div>

          {/* Table or State Messages */}
          {queryLoading ? (
            <div className="w-full min-h-[400px]">
              <Loader active />
            </div>
          ) : isYearOptionsEmpty && !shouldDisplaySampleData ? (
            displayYearOptionsEmptyMessage(insightsType)
          ) : error ? (
            displayErrorMessage()
          ) : insightsRows.length === 0 ? (
            displayMonthEmptyMessage(insightsType)
          ) : (
            <div className="w-full pt-4 overflow-x-auto">
              <Table className="w-full text-left">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell className="w-4/12">
                      <Text
                        variant={TypographyStyles.EYEBROW}
                        color={TextColors.MUTED}
                      >
                        Category
                      </Text>
                    </Table.HeaderCell>
                    {!isMobile && <Table.HeaderCell className="w-2/12" />}
                    {!isMobile && (
                      <Table.HeaderCell className="w-2/12 text-center">
                        <Text
                          variant={TypographyStyles.EYEBROW}
                          color={TextColors.MUTED}
                        >
                          PERCENT OF TOTAL
                        </Text>
                      </Table.HeaderCell>
                    )}
                    <Table.HeaderCell className="w-2/12 text-center">
                      <Text
                        variant={TypographyStyles.EYEBROW}
                        color={TextColors.MUTED}
                      >
                        AMOUNT
                      </Text>
                    </Table.HeaderCell>
                    <Table.HeaderCell className="w-2/12 text-center">
                      {!isMobile && (
                        <Text
                          variant={TypographyStyles.EYEBROW}
                          color={TextColors.MUTED}
                        >
                          TRANSACTIONS
                        </Text>
                      )}
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {currentPageInsightsRows.map((row) => (
                    <InsightsRow
                      key={row.transaction_category_id}
                      insightsRow={row}
                      isTopExpense={isTopExpense(
                        topExpenseCategoryIds,
                        row.transaction_category_id
                      )}
                      onTransactionsClick={() =>
                        handleSelectedExpenseBreakdown(row)
                      }
                      insightsType={insightsType}
                    />
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}
        </>
      )}

      {/* Footer Notes + Pagination */}
      <div className="w-full pt-4">
        <Text
          color={TextColors.MUTED}
          variant={TypographyStyles.BODY_SM}
          className="text-left"
        >
          Transactions shown are the most recent categorizations by your
          bookkeeper. If you see a transaction that needs to be recategorized,
          go to the{' '}
          <Link href="/transactions" newPage={false}>
            Transactions
          </Link>{' '}
          tab.
        </Text>
      </div>

      {pages > 1 && (
        <div className="w-full pt-4 flex justify-center">
          <PaginationPages
            currentPage={currentPage}
            onPageClick={handlePageChange}
            pages={selectedInsightsRow ? transactionsPages : pages}
          />
        </div>
      )}
    </>
  )
}
